import {
  ViewChild,
  TemplateRef,
  Component,
  ChangeDetectorRef,
  AfterContentInit,
  inject,
  OnDestroy,
  ChangeDetectionStrategy,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BreakpointObserver,
  Breakpoints,
  MediaMatcher,
} from '@angular/cdk/layout';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { Router, NavigationEnd, RouterModule } from '@angular/router';
import { environment } from '../../../environments/environment';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AuthService } from '../../util/services/auth.service';
import { PwaService } from '../../util/pwa/pwa.service';
import {
  ImageCropData,
  ImageCropDialogComponent,
} from '../shared/image-upload/image-crop-dialog.component';
import { compressImage } from '../shared/image-upload/image-upload-dialog.component';
import { backendMessage } from '../../state/shared/call-state';
import {
  getMatToolbar,
  getSidenavContent,
  smoothScrollTo,
} from 'src/app/util/dom/dom.utils';
import { ThemingService } from 'src/app/util/services/theming.service';
import { HelpComponent } from '../help/help.component';
import { WebConfigService } from '../../util/services/webconfig/webconfig.service';
import { AppCommunicationService } from 'src/app/util/services/app-communication/app-communication.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDrawerMode, MatSidenavModule } from '@angular/material/sidenav';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NotificationNavComponent } from './notification-nav/notification-nav.component';
import { LiveStatusComponent } from './live-status/live-status.component';
import { UsersApi, resourceUrls } from '../../api';
import { toNamedFile } from '../../util/binary-conversion';

import { NotifyService } from '../../shared/ui/notify.service';
import { PageTitleService } from '../../shared/ui/page-title.service';
import { DeviceService } from '../../util/services/device.service';
const DRAWER_PINNED_KEY = 'drawerPinned';

/** Gespeicherte Wahl „Seitenleiste angeheftet“ (Werte 'True'/'False' wie bisher). */
function readDrawerPinned(): boolean {
  try {
    return localStorage.getItem(DRAWER_PINNED_KEY) === 'True';
  } catch {
    return false;
  }
}

function writeDrawerPinned(pinned: boolean): void {
  try {
    localStorage.setItem(DRAWER_PINNED_KEY, pinned ? 'True' : 'False');
  } catch {
    // Speicher nicht verfügbar
  }
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatSidenavModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatToolbarModule,
    MatDialogModule,
    NotificationNavComponent,
    LiveStatusComponent,
  ],
})
export class NavComponent implements AfterContentInit, OnDestroy {
  isHandset$: Observable<boolean>;

  adminMenu: boolean = false;

  readonly production = environment.deploymentEnv === 'production';
  readonly device = inject(DeviceService);

  readonly logoUrl = resourceUrls.logo(256);
  userID!: number;
  title: string = '...';

  pageWhitelist: string[] = ['/login', '/', ''];

  currentTheme: any;

  mobileQuery: MediaQueryList;

  currentRoute: string = '';

  /** Seite gescrollt (Logo in der Toolbar durch „nach oben“ ersetzen). */
  readonly scrolled = signal(false);

  drawerPinned: boolean = false;

  drawerMode: MatDrawerMode = 'over';

  private sidenavScrollTarget: HTMLElement | null = null;

  @ViewChild('drawer', { static: false }) drawer: any;

  private breakpointObserver = inject(BreakpointObserver);

  private _usersApi = inject(UsersApi);
  public _login = inject(AuthService);
  private _router = inject(Router);
  private _dialog = inject(MatDialog);
  private _notify = inject(NotifyService);
  readonly _pageTitle = inject(PageTitleService);
  private _theme = inject(ThemingService);
  private _configService = inject(WebConfigService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private media = inject(MediaMatcher);
  public pwaS = inject(PwaService);
  public _com = inject(AppCommunicationService);

  constructor() {
    this.currentTheme = this._theme.theme;
    this._pageTitle.setTitle('Startseite');

    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe((event: NavigationEnd) => {
        var routeUrl = event.url.split('?')[0];
        // Open admin menu when user is in admin page
        if (routeUrl.includes('admin/')) this.adminMenu = true;

        this.currentRoute = routeUrl;
        if (!this.device.mobile) {
          this.applyDrawerPinned(
            readDrawerPinned() &&
              !this.pageWhitelist.includes(this.currentRoute),
          );
        }

        this.changeDetectorRef.markForCheck();
      });

    this.isHandset$ = this.breakpointObserver
      .observe(Breakpoints.Handset)
      .pipe(map((result) => result.matches));

    this.mobileQuery = this.media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => this.changeDetectorRef.detectChanges();
    this.mobileQuery.addEventListener('change', this._mobileQueryListener);
  }

  private _mobileQueryListener: () => void;
  subscription!: Subscription;

  // eslint-disable-next-line
  ngOnInit() {
    this._pageTitle.setHeading('Verwaltungsbereich');
    this.sidenavScrollTarget = getSidenavContent();
    if (this.sidenavScrollTarget) {
      this.sidenavScrollTarget.scrollTop = 0;
      this.sidenavScrollTarget.addEventListener('scroll', this.scrollHandler);
    }
    this._configService.getConfig().subscribe((result) => {
      this.title = this._configService.getConfigItem(result, 'app_name', '...');

      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    this.sidenavScrollTarget?.removeEventListener('scroll', this.scrollHandler);
  }

  ngAfterContentInit(): void {
    this._login.monitorSession();
    this._login.sessionExpired$.subscribe(() => {
      if (!this.pageWhitelist.includes(this.currentRoute)) {
        this.sessionDeprecated();
      }

      this.changeDetectorRef.markForCheck();
    });
  }

  scrollHandler = (event: Event): void => {
    const target = event.target as HTMLElement;
    const toolbarHeight = getMatToolbar()?.offsetHeight ?? 0;
    this.scrolled.set(target.scrollTop > toolbarHeight);
  };

  scrollTop() {
    const sidenav = getSidenavContent();
    if (sidenav) {
      smoothScrollTo(sidenav, 0, 300);
    }
  }

  installPwa(): void {
    this.pwaS.promptEvent.prompt();
  }

  /** Profilbild wählen, quadratisch zuschneiden (128 px) und hochladen. */
  async uploadAvatar($event: Event): Promise<void> {
    const input = $event.target as HTMLInputElement;
    this.drawer.toggle();
    const cropped = await firstValueFrom(
      this._dialog
        .open<ImageCropDialogComponent, ImageCropData, Blob | undefined>(
          ImageCropDialogComponent,
          {
            disableClose: true,
            data: {
              title: 'Bild zuschneiden',
              event: $event,
              aspectRatio: 1,
              resizeToWidth: 128,
              rotate: false,
            },
          },
        )
        .afterClosed(),
    );
    this.changeDetectorRef.markForCheck();
    input.value = '';
    try {
      if (!cropped) return;
      const data = await firstValueFrom(
        this._usersApi.uploadUserAvatar({
          id: 'me',
          file: toNamedFile(await compressImage(cropped), 'avatar'),
        }),
      );
      this.changeDetectorRef.markForCheck();
      if (data?.avatar) {
        this._login.setAvatar(data.avatar);
        this._notify.toast('Profilbild wurde aktualisiert.', 2000, 'success');
      }
    } catch (error) {
      this._notify.toast(
        backendMessage(error) ?? 'Profilbild konnte nicht gespeichert werden.',
        3000,
        'error',
      );
    } finally {
      this.drawer.toggle();
      this.changeDetectorRef.markForCheck();
    }
  }

  // Logout popup
  logout() {
    const popup = this._notify.confirm({
      title: 'Ausloggen?',
      text: 'Möchtest du dich wirklich ausloggen?',
      icon: 'question',
      confirmText: 'Ja, ausloggen',
      cancelText: 'Nein',
    });
    popup.then((result) => {
      if (result) {
        this._com.sendAction('logout');
        this._login.logout();
      }

      this.changeDetectorRef.markForCheck();
    });
  }

  sessionDeprecated() {
    const popup = this._notify.alert({
      title: 'Sitzung abgelaufen',
      text: 'Bitte melde dich neu an',
      icon: 'error',
      blocking: true,
      buttonText: 'Erneut anmelden',
    });
    popup.then(() => {
      const __result = this._router.navigate(['/login']);
      this.changeDetectorRef.markForCheck();
      return __result;
    });
  }

  pinDrawer() {
    const pinned = !this.drawerPinned;
    this.applyDrawerPinned(pinned);
    writeDrawerPinned(pinned);
    this._notify.toast(
      pinned ? 'Seitenleiste angepinnt' : 'Seitenleiste gelöst',
      2000,
      'warning',
    );
  }

  closeDrawer() {
    if (this.drawerPinned) {
      this.applyDrawerPinned(false);
      writeDrawerPinned(false);
      this._notify.toast('Seitenleiste gelöst', 2000, 'warning');
    }
    this.drawer.close();
  }

  /** Seitenleiste anheften (neben dem Inhalt) oder lösen (überlagert ihn). */
  private applyDrawerPinned(pinned: boolean) {
    this.drawerPinned = pinned;
    this.drawerMode = pinned ? 'side' : 'over';
    getSidenavContent()?.classList.toggle('pinnedDrawer', pinned);
  }

  checkDrawer() {
    if (!this.drawerPinned) this.drawer.toggle();
  }

  openHelp() {
    const helpDialog = this._dialog.open(HelpComponent, {
      disableClose: true,
      width: 'min(94vw, 720px)',
      maxWidth: '94vw',
      autoFocus: false,
      panelClass: 'help-dialog-panel',
    });
    helpDialog
      .keydownEvents()
      .pipe(filter((e: KeyboardEvent) => e.code === 'Escape'))
      .subscribe(() => helpDialog.close());
  }
}
